import { PrintPDFTypeEnum } from 'commons/enums';
import { PRIMARY_RED } from 'commons/styles/colors';
import {
  ICompanyInfo,
  KoliInfoUI,
  OrderType,
  PickingTask,
} from 'commons/types';
import { IPackingTask } from 'commons/types/packingTask.interface';
import { setShowPrintSPOnlyButton } from 'redux-stores/reducers/packingTaskReducer';
import { snackbarSetData } from 'redux-stores/reducers/utilityReducer';
import { OUTBOUND_PRINT_TEMPLATE_ROUTES } from 'routes/constants';
import { swiperxWmsApiDeliveryNote } from 'services/apis/SwipeRxWmsApiDeliveryNote';
import { swipeRxWmsAPIPackingTask } from 'services/apis/SwipeRxWmsApiPackingTask';

import { mapInvoicePrintData } from './InvoicePrintDataMapper.util';
import { mapShippingLabelPrintData } from './ShippingLabelPrintDataMapper.util';

const _getPrintURL = (
  packingTask: IPackingTask,
  company: ICompanyInfo | null,
  koli: KoliInfoUI[],
  type: PrintPDFTypeEnum,
) => {
  switch (type) {
    case PrintPDFTypeEnum.INVOICE:
      return `${OUTBOUND_PRINT_TEMPLATE_ROUTES.INVOICE_PACK}?&skip-alert-notif=true`;
    case PrintPDFTypeEnum.INVOICE_ESP:
      return `${OUTBOUND_PRINT_TEMPLATE_ROUTES.INVOICE_ESP}?&skip-alert-notif=true`;
    case PrintPDFTypeEnum.SHIPPING_LABEL:
      return `${
        OUTBOUND_PRINT_TEMPLATE_ROUTES.SHIP_LABEL
      }?koli=${JSON.stringify(
        koli,
      )}&skip-alert-notif=true&data=${encodeURIComponent(
        JSON.stringify(
          mapShippingLabelPrintData(packingTask.picking_task as PickingTask),
        ),
      )}`;
    default:
      return '/';
  }
};

export const printPDF = async (
  packingTask: IPackingTask,
  company: ICompanyInfo | null,
  koli: KoliInfoUI[],
  type: PrintPDFTypeEnum,
  dispatch: any,
  withDeliveryNoteMerge = false,
) => {
  if (type === PrintPDFTypeEnum.ESP) {
    const url = await swipeRxWmsAPIPackingTask.getESPDocument(
      packingTask.po_number,
    );
    if (!url) {
      dispatch(
        snackbarSetData({
          open: true,
          message: 'Document URL not found for this task',
          color: PRIMARY_RED,
        }),
      );
    } else {
      const hrefArea = document.createElement('a');
      hrefArea.href = url;
      hrefArea.target = '_blank';
      hrefArea.click();
    }
  } else {
    // special case for invoice. since data may large,
    // we store value temporarily in local storage
    // removed upon iframe destroyed
    if (type === PrintPDFTypeEnum.INVOICE) {
      localStorage.setItem(
        'invoice',
        JSON.stringify(mapInvoicePrintData(packingTask, company)),
      );
      if (withDeliveryNoteMerge) {
        const deliveryNote =
          await swiperxWmsApiDeliveryNote.getDeliveryNoteByPoNumber(
            packingTask.po_number,
          );
        if (deliveryNote) {
          localStorage.setItem('delivery-note', JSON.stringify(deliveryNote));
        }
      }
    } else if (type === PrintPDFTypeEnum.INVOICE_ESP) {
      const mappedData = mapInvoicePrintData(packingTask, company);

      if (
        [OrderType.PRECURSOR, OrderType.PSIKOTROPIKA, OrderType.OOT].includes(
          mappedData.po_type,
        ) &&
        !packingTask.picking_task.is_digital_signed
      ) {
        const content =
          await swipeRxWmsAPIPackingTask.getESPDocumentWithContentBase64(
            packingTask.po_number,
          );
        if (content) {
          localStorage.setItem('esp-content-base64', JSON.stringify(content));
        } else {
          dispatch(
            snackbarSetData({
              open: true,
              message:
                'Error on fetching SP document. Still print other documents',
              color: PRIMARY_RED,
            }),
          );
          // open print esp only button
          dispatch(setShowPrintSPOnlyButton(true));
        }
      }

      if (withDeliveryNoteMerge) {
        const deliveryNote =
          await swiperxWmsApiDeliveryNote.getDeliveryNoteByPoNumber(
            packingTask.po_number,
          );
        if (deliveryNote) {
          localStorage.setItem('delivery-note', JSON.stringify(deliveryNote));
        }
      }

      localStorage.setItem('invoice', JSON.stringify(mappedData));
    }
    // trigger print
    const printedArea = document.createElement('iframe');
    printedArea.src = _getPrintURL(packingTask, company, koli, type);
    printedArea.hidden = true;
    document.body.appendChild(printedArea);
    printedArea.addEventListener('load', () => {
      setTimeout(() => {
        const printedContentWindow =
          printedArea.contentDocument || printedArea.contentWindow?.document;

        const anchorName =
          type === PrintPDFTypeEnum.SHIPPING_LABEL ? 'img' : '#inv-sign';
        const anchorImg: HTMLImageElement | null | undefined =
          printedContentWindow?.querySelector(anchorName);

        const printAndCleanup = () => {
          printedArea?.contentWindow?.focus();
          printedArea?.contentWindow?.print();
          setTimeout(() => {
            document.body.removeChild(printedArea);
            localStorage.removeItem('invoice');
          }, 2000);
        };

        if (packingTask.picking_task.verify_document_url) {
          printAndCleanup();
        } else if (anchorImg) {
          if (anchorImg.complete) {
            printAndCleanup();
          } else {
            anchorImg.addEventListener('load', printAndCleanup);
          }
        }
      }, 1000);
    });
  }
};
